import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { SecondaryButton } from '../components/SecondaryButton';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import {
  LocationAutocomplete,
  type SelectedPlace,
} from '../components/LocationAutocomplete';

import {
  listAvailablePros,
  listCategories,
  listNearestAvailablePros,
  listServices,
  type AvailabilitySlot,
  type AvailablePro,
  type ServiceCategory,
  type ServiceItem,
} from '../../lib/booking-api';
import {
  getBookingDraft,
  patchBookingDraft,
  useBookingDraft,
} from '../../lib/booking-draft';
import { useCustomerAuth } from '../../lib/use-customer-auth';
import { ukSlotToISO, isoToUkDateOnly } from '../../lib/uk-time';
import { toast } from '../../lib/toast';

type TimeOfDay = 'All' | 'Morning' | 'Afternoon' | 'Evening';

// "Morning" = 06:00–11:59, "Afternoon" = 12:00–17:59,
// "Evening"   = 18:00–23:59. Threshold-based so a pro with a 17:30
// slot still counts as Afternoon.
function slotMatchesTimeOfDay(slot: AvailabilitySlot, t: TimeOfDay): boolean {
  if (t === 'All') return true;
  const hh = parseInt(slot.start.slice(0, 2), 10);
  if (t === 'Morning') return hh < 12;
  if (t === 'Afternoon') return hh >= 12 && hh < 18;
  return hh >= 18;
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Combine the day the customer is browsing with a slot's "HH:MM" into a
// local ISO datetime. With no slot it falls to midnight (date only).
// Used so picking a pro's time slot here carries the appointment
// date+time forward — the checkout screen then opens on review/pay
// instead of asking for the time again at the end (C3).
function buildScheduledStartIso(date: Date, slotStart?: string): string {
  // Anchor the slot to UK (Europe/London) wall-clock regardless of the
  // browser's timezone (see lib/uk-time) — a non-UK browser's local setHours
  // shifted the booking, and the checkout slot re-validation then rejected it
  // ("professional no longer available" → "Could not start checkout").
  return ukSlotToISO(date, slotStart || '00:00');
}

export default function SelectProfessionalPage() {
  const navigate = useNavigate();
  const draft = useBookingDraft();
  const { isAuthenticated } = useCustomerAuth();

  // Filter strip state. Service + category are derived from the
  // draft once the catalog has loaded; date defaults to the draft's
  // scheduled_start (or +7 days), and the time-of-day filter
  // narrows the slot list shown on each card.
  const [date, setDate] = useState<Date | undefined>(
    // UK calendar day (not browser-local) so the round-trip with
    // buildScheduledStartIso/ukSlotToISO stays on the same UK day.
    draft?.scheduled_start ? isoToUkDateOnly(draft.scheduled_start) : undefined,
  );
  const [time, setTime] = useState<TimeOfDay>('All');
  // Lets the customer switch service right here without going back a
  // step. Null = fall back to the draft-resolved service.
  const [serviceOverrideId, setServiceOverrideId] = useState<string | null>(
    null,
  );
  // Search box inside the service dropdown.
  const [serviceSearch, setServiceSearch] = useState('');
  // Editable location — seeded from the booking draft. Changing it
  // re-runs the pros query (radius filter keys off lat/lng).
  const [locationText, setLocationText] = useState(
    draft?.address?.address_line1 ?? '',
  );

  // Persist a newly-picked place to the draft so the availability query
  // (which reads draft.address.lat/lng) refreshes and the rest of the
  // wizard keeps the same address.
  const handlePlaceSelected = (place: SelectedPlace) => {
    setLocationText(place.description);
    patchBookingDraft({
      address: {
        address_line1: place.description,
        address_line2: null,
        city: place.city ?? null,
        postcode: place.postcode ?? null,
        country_code: place.country_code ?? null,
        latitude: place.latitude ?? null,
        longitude: place.longitude ?? null,
      },
    });
  };

  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [catalogReady, setCatalogReady] = useState(false);

  const [pros, setPros] = useState<AvailablePro[]>([]);
  const [loading, setLoading] = useState(false);

  // Load catalog once so we can resolve the draft's service_code →
  // real service id and pull pricing/duration. Cheap public call.
  useEffect(() => {
    (async () => {
      try {
        // Scope the service fetch to the category the customer is booking
        // in (mirrors SelectTreatmentPage's call). The all-categories
        // fetch this page used to make was both wasteful — the service
        // dropdown only ever lists the current category — and fragile:
        // if that larger payload came back empty/failed, the service
        // couldn't resolve and the page stranded on "—" / "Coming soon"
        // even though the customer had just picked a service. (Issue 8)
        const categoryId = getBookingDraft()?.service_category_id ?? undefined;
        const [cats, svcs] = await Promise.all([
          listCategories(),
          listServices(categoryId),
        ]);
        setCategories(cats.data);
        setServices(svcs.data);
      } catch (err) {
        console.warn('Catalog load failed', err);
      } finally {
        setCatalogReady(true);
      }
    })();
  }, []);

  // Resolve the service the customer chose in the prior wizard step.
  // BookingFlowPage writes draft.service_id explicitly; the
  // service_code branch and first-service fallback are kept for
  // direct-nav cases (no draft in localStorage).
  const resolved = useMemo(() => {
    if (!catalogReady) return null;
    let svc: ServiceItem | undefined;
    // An explicit in-page override wins over the draft.
    if (serviceOverrideId) {
      svc = services.find((s) => s.id === serviceOverrideId);
    }
    if (!svc && draft?.service_id) {
      svc = services.find((s) => s.id === draft.service_id);
    }
    if (!svc && draft?.meta?.service_code) {
      const code = String(draft.meta.service_code);
      svc = services.find((s) => s.code === code);
    }
    if (!svc) svc = services[0];
    const cat = svc
      ? categories.find((c) => c.id === svc.category.id)
      : undefined;
    return { service: svc, category: cat };
  }, [catalogReady, draft, categories, services, serviceOverrideId]);

  // Default the date if the draft didn't supply one.
  useEffect(() => {
    if (!date) {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      setDate(d);
    }
  }, [date]);

  // Pull pros + slots whenever the service, date, or saved customer
  // location moves. The lat/lng on the draft drives the radius
  // filter — pros further than their travel_radius_miles away are
  // excluded server-side. Missing coords → no filter.
  // 3 alternative pros surfaced as a fallback when nobody is free
  // on the customer's chosen date. Each carries their slot map so
  // the customer can pick a new date inline without leaving the page.
  const [nearestAlternatives, setNearestAlternatives] = useState<
    Array<AvailablePro & { next_slot_at: string | null }>
  >([]);
  const [alternativesLoading, setAlternativesLoading] = useState(false);
  // 'service' = same service different date; 'category' = the backend
  // widened to any sibling service in the same category because nobody
  // offered the exact one. Lets us reword the heading so the customer
  // understands what they're seeing instead of just "Closest alternatives".
  const [alternativesKind, setAlternativesKind] = useState<
    'service' | 'category' | null
  >(null);
  // The service to fetch availability for. Prefer the catalog-resolved
  // service (it honours an in-page override and the draft), but fall back
  // to the raw id the customer already chose in the draft so the pro grid
  // still loads when the catalog is slow, empty, or failed to fetch —
  // rather than gating availability behind catalog resolution and
  // stranding the customer on a false "Coming soon to your area". (Issue 8)
  const queryServiceId = resolved?.service?.id ?? draft?.service_id ?? null;
  const queryDuration =
    draft?.duration_minutes ?? resolved?.service?.duration_minutes ?? 60;

  useEffect(() => {
    if (!queryServiceId || !date) return;
    setLoading(true);
    const lat = draft?.address?.latitude ?? null;
    const lng = draft?.address?.longitude ?? null;
    const city = draft?.address?.city ?? null;
    listAvailablePros({
      service_id: queryServiceId,
      date_from: ymd(date),
      date_to: ymd(date),
      duration_minutes: queryDuration,
      lat,
      lng,
      city,
    })
      .then((res) => {
        setPros(res.data);
        // Only fetch alternatives when the primary listing is empty —
        // otherwise the customer already has plenty of options.
        if ((res.data ?? []).length === 0) {
          setAlternativesLoading(true);
          listNearestAvailablePros({
            service_id: queryServiceId,
            target_date: ymd(date),
            duration_minutes: queryDuration,
            lat,
            lng,
            city,
            days_ahead: 30,
            limit: 3,
          })
            .then((alt) => {
              setNearestAlternatives(alt.data ?? []);
              setAlternativesKind(alt.fallback_kind ?? 'service');
            })
            .catch(() => {
              setNearestAlternatives([]);
              setAlternativesKind(null);
            })
            .finally(() => setAlternativesLoading(false));
        } else {
          setNearestAlternatives([]);
          setAlternativesKind(null);
        }
      })
      .catch((err: any) => {
        const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to load';
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  }, [
    queryServiceId,
    date,
    queryDuration,
    draft?.address?.latitude,
    draft?.address?.longitude,
  ]);

  // Service dropdown list — restricted to the currently-resolved
  // category so a customer who started in "Hair" can only swap to
  // other Hair services, not jump into Cleaning / Childcare from this
  // picker. (Cross-category jumps belong in /select-treatment, not
  // here.) Falls through to the full list only when no category has
  // been resolved yet — guards a transient empty state during the
  // initial catalog load. Then sorts A→Z and applies the search-box
  // substring filter.
  const filteredServices = useMemo(() => {
    const q = serviceSearch.trim().toLowerCase();
    const categoryId = resolved?.category?.id ?? null;
    const scoped = categoryId
      ? services.filter((s) => s.category?.id === categoryId)
      : services;
    const sorted = [...scoped].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
    if (!q) return sorted;
    return sorted.filter((s) => s.name.toLowerCase().includes(q));
  }, [services, serviceSearch, resolved?.category?.id]);

  // Filter each pro's slot list by the time-of-day chip. A pro with
  // zero matching slots disappears from the grid for that filter.
  const filteredPros = useMemo(() => {
    const dateKey = date ? ymd(date) : null;
    return pros
      .map((p) => {
        const slots = dateKey ? p.slots_by_date[dateKey] ?? [] : [];
        const matching = slots.filter((s) => slotMatchesTimeOfDay(s, time));
        return { ...p, _slots: matching };
      })
      .filter((p) => p._slots.length > 0);
  }, [pros, date, time]);

  // Clicking a pro opens their profile. Carry the (possibly overridden)
  // service + category + duration into the draft so the profile and the
  // downstream schedule step resolve the right service.
  //
  // C3: also carry the appointment date the customer is browsing — and,
  // when they tapped a specific time slot, the exact time — so time is
  // chosen here (early + clearly) rather than buried at checkout. The
  // YourAppointmentPage opens on review/pay when a real time is set, or
  // on the time-picker (date pre-filled) when only the day is known.
  const openProProfile = (proId: string, slotStart?: string) => {
    const base = {
      professional_id: proId,
      mode: draft?.mode ?? 'ONE_OFF',
      service_id: resolved?.service?.id ?? draft?.service_id ?? null,
      service_category_id:
        resolved?.category?.id ?? draft?.service_category_id ?? null,
      duration_minutes:
        draft?.duration_minutes ??
        resolved?.service?.duration_minutes ??
        60,
    };
    // Keep the stashed display names in lockstep with service_id — if the
    // customer switched service via the in-page dropdown, the draft's name
    // must follow so the heading fallback never shows a stale service.
    // Preserve the prior names when the catalog hasn't resolved. (Issue 8)
    const metaNames = {
      service_name: resolved?.service?.name ?? draft?.meta?.service_name ?? null,
      service_category_name:
        resolved?.category?.name ?? draft?.meta?.service_category_name ?? null,
    };
    if (date) {
      patchBookingDraft({
        ...base,
        scheduled_start: buildScheduledStartIso(date, slotStart),
        meta: {
          ...(draft?.meta ?? {}),
          ...metaNames,
          date: ymd(date),
          times: slotStart ? [slotStart.slice(0, 5)] : [],
        },
      });
    } else {
      patchBookingDraft({
        ...base,
        meta: { ...(draft?.meta ?? {}), ...metaNames },
      });
    }
    // Continue the booking in the SAME tab. The chosen service / date / slot
    // all live in the booking draft (localStorage), so navigating in-place
    // keeps the session intact, and the customer returns here via Back or the
    // "browse other professionals" link on the profile. (Hair & Beauty /
    // Wellness — Issue 9: flow must stay in one tab.)
    navigate(`/public-profile?id=${proId}`);
  };

  // Prefer the live catalog name, but fall back to the name the previous
  // step stashed on the draft so the heading reads the chosen service even
  // when the catalog fetch is slow/empty/failed — never a bare "—". (Issue 8)
  const serviceLabel =
    resolved?.service?.name ?? draft?.meta?.service_name ?? '—';
  const categoryLabel =
    resolved?.category?.name ?? draft?.meta?.service_category_name ?? '';
  // Service duration so the customer can see how long the appointment runs
  // while choosing a pro/slot — previously only surfaced at the review step.
  const durationMins =
    resolved?.service?.duration_minutes ?? draft?.duration_minutes ?? null;
  const durationLabel = durationMins
    ? durationMins % 60 === 0
      ? `${durationMins / 60}h`
      : durationMins < 60
        ? `${durationMins}m`
        : `${Math.floor(durationMins / 60)}h ${durationMins % 60}m`
    : '';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <div className="max-w-[1140px] mx-auto w-full px-4 pt-6">
        <SecondaryButton
          onClick={() => navigate(-1)}
          icon={<span className="material-icons text-[18px]">navigate_before</span>}
        >
          Back
        </SecondaryButton>
      </div>

      <main className="flex-1 max-w-[1140px] mx-auto px-4 py-10 pb-32 md:pb-16 w-full">
        <div className="text-center mb-4">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px] md:text-[24px] leading-[36px] md:leading-[54px] tracking-[-0.1px] mb-4">
            Select a professional
          </h1>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
            Showing pros available for{' '}
            <span className="font-bold text-[#3a3335]">{serviceLabel}</span>
            {categoryLabel ? ` (${categoryLabel})` : ''}
            {durationLabel ? (
              <>
                {' · approx '}
                <span className="font-bold text-[#3a3335]">{durationLabel}</span>
              </>
            ) : null}{' '}
            on the selected day —{' '}
            <span className="font-bold text-[#3a3335]">tap a time to book</span>.
          </p>
        </div>

        {/* Filter Bar */}
        <div className="max-w-[820px] mx-auto mb-16">
          <div className="bg-white border border-[#eee] rounded-[12px] shadow-sm flex flex-col md:flex-row items-stretch overflow-hidden">
            {/* Service — changeable here so the customer can switch
                without going back a step. */}
            <Popover>
              <PopoverTrigger asChild>
                <div className="flex-1 border-b md:border-b-0 md:border-r border-[#eee] p-4 flex flex-col justify-center relative group hover:bg-gray-50/50 transition-colors cursor-pointer">
                  <label className="text-[12px] text-[#908e8e] font-['DM_Sans',sans-serif] mb-1">
                    Service
                  </label>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[15px] truncate">
                      {serviceLabel}
                    </span>
                    <span className="material-icons text-[#3a3335] text-[18px] shrink-0">
                      expand_more
                    </span>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0 rounded-[12px] overflow-hidden" align="start">
                {/* Search box pinned to the top of the list. */}
                <div className="p-2 border-b border-[#f0f0f0]">
                  <div className="flex items-center gap-2 bg-[#f7f5f1] rounded-[8px] px-3 h-[38px]">
                    <span className="material-icons text-[#908e8e] text-[18px]">
                      search
                    </span>
                    <input
                      autoFocus
                      type="text"
                      value={serviceSearch}
                      onChange={(e) => setServiceSearch(e.target.value)}
                      placeholder="Search services…"
                      className="flex-1 bg-transparent outline-none font-['DM_Sans',sans-serif] text-[14px] text-[#3a3335] placeholder:text-[#908e8e]"
                    />
                    {serviceSearch ? (
                      <button
                        type="button"
                        onClick={() => setServiceSearch('')}
                        className="material-icons text-[#908e8e] text-[18px] hover:text-[#8e2a2f]"
                        aria-label="Clear search"
                      >
                        close
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="max-h-[300px] overflow-y-auto py-1">
                  {services.length === 0 ? (
                    <p className="px-4 py-3 text-[13px] text-[#908e8e] font-['DM_Sans',sans-serif]">
                      Loading services…
                    </p>
                  ) : filteredServices.length === 0 ? (
                    <p className="px-4 py-6 text-[13px] text-[#908e8e] font-['DM_Sans',sans-serif] text-center">
                      No services match "{serviceSearch}".
                    </p>
                  ) : (
                    filteredServices.map((s) => {
                      const active = resolved?.service?.id === s.id;
                      const catName = categories.find(
                        (c) => c.id === s.category.id,
                      )?.name;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => {
                            setServiceOverrideId(s.id);
                            setServiceSearch('');
                          }}
                          className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-2 font-['DM_Sans',sans-serif] transition-colors ${
                            active
                              ? 'bg-[#8e2a2f] text-white'
                              : 'text-[#3a3335] hover:bg-[#f2eee2]'
                          }`}
                        >
                          <span className="min-w-0">
                            <span
                              className={`block text-[14px] font-bold truncate ${active ? 'text-white' : 'text-[#3a3335]'}`}
                            >
                              {s.name}
                            </span>
                            {catName ? (
                              <span
                                className={`block text-[11px] truncate ${active ? 'text-white/70' : 'text-[#908e8e]'}`}
                              >
                                {catName}
                              </span>
                            ) : null}
                          </span>
                          {active ? (
                            <span className="material-icons text-[18px] shrink-0">
                              check
                            </span>
                          ) : null}
                        </button>
                      );
                    })
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* Location — editable; re-queries pros by radius. */}
            <div className="flex-1 border-b md:border-b-0 md:border-r border-[#eee] flex items-center">
              <LocationAutocomplete
                label="Location"
                value={locationText}
                onChange={setLocationText}
                onPlaceSelected={handlePlaceSelected}
                containerHeight="h-[72px]"
                placeholder="Postcode or area"
                className="!border-0"
              />
            </div>

            {/* Date */}
            <Popover>
              <PopoverTrigger asChild>
                <div className="flex-1 border-b md:border-b-0 md:border-r border-[#eee] p-4 flex flex-col justify-center relative group hover:bg-gray-50/50 transition-colors cursor-pointer">
                  <label className="text-[12px] text-[#908e8e] font-['DM_Sans',sans-serif] mb-1">
                    Select date
                  </label>
                  <div className="flex items-center justify-between">
                    <span className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[15px]">
                      {date ? format(date, 'dd MMM yyyy') : 'Select date'}
                    </span>
                    <span className="material-icons text-[#3a3335] text-[20px]">
                      calendar_today
                    </span>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  initialFocus
                  className="bg-white"
                  disabled={(d) => {
                    // Block past dates — no pro is available
                    // yesterday.
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return d.getTime() < today.getTime();
                  }}
                />
              </PopoverContent>
            </Popover>

            {/* Time of day */}
            <div className="flex-1 p-4 flex items-center gap-4">
              <Popover>
                <PopoverTrigger asChild>
                  <div className="flex-1 flex flex-col justify-center relative group hover:bg-gray-50/50 transition-colors cursor-pointer">
                    <label className="text-[12px] text-[#908e8e] font-['DM_Sans',sans-serif] mb-1">
                      Time of day
                    </label>
                    <div className="flex items-center justify-between">
                      <span className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[15px]">
                        {time}
                      </span>
                      <span className="material-icons text-[#3a3335] text-[18px]">
                        expand_more
                      </span>
                    </div>
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-[180px] p-0 rounded-[12px] overflow-hidden" align="end">
                  <div className="flex flex-col py-1">
                    {(['All', 'Morning', 'Afternoon', 'Evening'] as TimeOfDay[]).map(
                      (t) => {
                        const active = time === t;
                        return (
                          <button
                            key={t}
                            onClick={() => setTime(t)}
                            className={`px-4 py-2.5 text-left flex items-center justify-between gap-2 font-['DM_Sans',sans-serif] text-[14px] transition-colors ${
                              active
                                ? 'bg-[#8e2a2f] text-white font-bold'
                                : 'text-[#3a3335] hover:bg-[#f2eee2] hover:text-[#8e2a2f]'
                            }`}
                          >
                            {t}
                            {active ? (
                              <span className="material-icons text-[18px]">
                                check
                              </span>
                            ) : null}
                          </button>
                        );
                      },
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        {/* Pros grid */}
        {loading ? (
          <p className="text-center text-sm text-[#908e8e] py-16 mb-8">
            Looking for available pros…
          </p>
        ) : filteredPros.length === 0 ? (
          <div className="mb-16 max-w-[820px] mx-auto">
            <div className="text-center text-sm text-[#908e8e] py-10">
              No pros available for this service{' '}
              {date ? `on ${format(date, 'dd MMM yyyy')}` : ''}
              {time !== 'All' ? ` in the ${time.toLowerCase()}` : ''}.
            </div>
            {/* "Closest alternatives" panel — 3 pros with the soonest
                slot at-or-after the customer's chosen date. Each pro
                row shows up to 6 alternative slots so the customer can
                pick a new date inline. Backed by GET /public/availability/nearest-pros. */}
            {alternativesLoading ? (
              <p className="text-center text-sm text-[#908e8e] pb-8">
                Looking for the closest alternatives…
              </p>
            ) : nearestAlternatives.length > 0 ? (
              <>
                <h3 className="text-center font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[20px] leading-[28px] mb-2">
                  {alternativesKind === 'category'
                    ? `Other ${categoryLabel || 'category'} professionals available`
                    : 'Closest alternatives'}
                </h3>
                {alternativesKind === 'category' ? (
                  <p className="text-center font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] mb-6">
                    No pro currently offers {serviceLabel} — here are pros who
                    work in the same category and have nearby availability.
                  </p>
                ) : (
                  <p className="text-center font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] mb-6">
                    Same service, soonest available date.
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {nearestAlternatives.map((pro) => {
                    const displayName =
                      [pro.user?.first_name, pro.user?.last_name]
                        .filter(Boolean)
                        .join(' ') ||
                      pro.business_name ||
                      '—';
                    // Flatten slots_by_date into a chronological list,
                    // capped at 6 so the card stays scannable.
                    const altSlots = Object.entries(pro.slots_by_date)
                      .flatMap(([d, list]) =>
                        (list ?? []).map((s) => ({ date: d, ...s })),
                      )
                      .sort((a, b) =>
                        `${a.date}T${a.start}`.localeCompare(
                          `${b.date}T${b.start}`,
                        ),
                      )
                      .slice(0, 6);
                    return (
                      <div
                        key={pro.id}
                        className="bg-white border border-[#ede8e9] rounded-[12px] p-5 flex flex-col gap-3"
                      >
                        <div
                          onClick={() => openProProfile(pro.id)}
                          className="flex items-center gap-3 cursor-pointer"
                        >
                          <div className="w-[48px] h-[48px] rounded-full overflow-hidden bg-[#f2eee2] flex items-center justify-center shrink-0">
                            {pro.cover_image_preview_url ??
                            pro.cover_image_url ? (
                              <img
                                src={
                                  pro.cover_image_preview_url ??
                                  pro.cover_image_url ??
                                  ''
                                }
                                alt={displayName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px]">
                                {displayName.slice(0, 1).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] truncate">
                              {displayName}
                            </p>
                            {pro.next_slot_at ? (
                              <p className="text-[12px] text-[#8e2a2f] font-bold">
                                Earliest:{' '}
                                {format(
                                  new Date(pro.next_slot_at),
                                  'EEE d MMM, HH:mm',
                                )}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        {altSlots.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {altSlots.map((s) => (
                              <span
                                key={`${s.date}-${s.start}`}
                                className="inline-flex items-center rounded-md border border-[#ede8e9] bg-[#fcfaf7] px-2 py-1 text-[11px] text-[#3a3335]"
                              >
                                {format(
                                  new Date(`${s.date}T${s.start}`),
                                  'd MMM, HH:mm',
                                )}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => openProProfile(pro.id)}
                          className="mt-1 self-start text-[12px] font-bold text-[#8e2a2f] underline"
                        >
                          See full availability →
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              // No pros for this service AND no nearby alternatives on any
              // date — i.e. we genuinely don't cover this area yet. Show a
              // warm "coming soon" message rather than a bare empty page.
              <div className="text-center py-6">
                <span className="material-icons text-[#8e2a2f] text-[40px] mb-2">
                  storefront
                </span>
                <p className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] leading-[26px] mb-1">
                  Coming soon to your area
                </p>
                <p className="text-sm text-[#908e8e] max-w-[420px] mx-auto">
                  No professionals available in your area just yet — we're
                  adding new pros all the time, so please check back soon!
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-[15px] md:gap-[20px] mb-16 max-w-[820px] mx-auto">
            {filteredPros.map((pro) => {
              const displayName =
                [pro.user?.first_name, pro.user?.last_name]
                  .filter(Boolean)
                  .join(' ') ||
                pro.business_name ||
                '—';
              return (
                <div
                  key={pro.id}
                  onClick={() => openProProfile(pro.id)}
                  className="relative bg-[#f2eee2]/40 rounded-[12px] p-[16px] flex flex-col items-center cursor-pointer transition-all border-2 border-transparent hover:border-[#8e2a2f] hover:bg-white"
                >
                  <div className="w-[90px] h-[90px] rounded-full overflow-hidden mb-4 mt-2 ring-4 ring-white shadow-sm bg-[#f2eee2] flex items-center justify-center">
                    {pro.cover_image_preview_url ?? pro.cover_image_url ? (
                      <img
                        src={pro.cover_image_preview_url ?? pro.cover_image_url ?? ''}
                        alt={displayName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[28px]">
                        {displayName.slice(0, 1).toUpperCase()}
                      </span>
                    )}
                  </div>

                  <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[20px] leading-[28px] mb-1 text-center">
                    {displayName}
                  </h3>

                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="material-icons text-[#8e2a2f] text-[18px]">
                      star
                    </span>
                    <span className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[13px]">
                      {pro.rating_avg ? Number(pro.rating_avg).toFixed(2) : '—'}
                    </span>
                    <span className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px]">
                      ({pro.rating_count})
                    </span>
                  </div>

                  {pro.minimum_booking_value &&
                  pro.minimum_booking_value > 0 ? (
                    <div className="mb-2 text-[11px] font-medium text-[#8e2a2f] text-center">
                      Min booking £
                      {Number(pro.minimum_booking_value).toFixed(2)}
                    </div>
                  ) : null}

                  {/* C3: pick a time here. Tapping a slot selects this
                      pro AT that time and jumps straight to review/pay;
                      tapping the card (not a slot) opens the profile to
                      choose a time there. Cap the chips so cards stay
                      scannable. */}
                  <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[11px] text-center mb-1">
                    {pro._slots.length === 1
                      ? '1 time available'
                      : `${pro._slots.length} times available`}
                  </p>
                  <div className="flex flex-wrap justify-center gap-1.5 w-full">
                    {pro._slots.slice(0, 4).map((slot) => (
                      <button
                        key={slot.start}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openProProfile(pro.id, slot.start);
                        }}
                        className="px-2 py-1 rounded-[6px] border border-[#ede8e9] bg-white text-[12px] font-bold text-[#8e2a2f] hover:bg-[#8e2a2f] hover:text-white transition-colors"
                      >
                        {slot.start.slice(0, 5)}
                      </button>
                    ))}
                    {pro._slots.length > 4 ? (
                      <span className="px-2 py-1 text-[12px] text-[#908e8e] self-center">
                        +{pro._slots.length - 4}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
